import React, { useState, useEffect } from 'react';
import {
  Row,
  Col,
  Card,
  CardBody,
  Container,
  Spinner,
  Button,
} from 'reactstrap';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useSelector } from 'react-redux';
import { selectCountry } from '../../selectors/country';

import Modal from '../../components/Common/Modal/Modal';
import Breadcrumbs from '../../components/Common/Breadcrumb';
import TokenStatusCard from '../../components/FacebookToken/TokenStatusCard';
import TokenSummary from '../../components/FacebookToken/TokenSummary';
import ManualTokenRenewal from '../../components/FacebookToken/ManualTokenRenewal';
import CreateTokenForm from '../../components/FacebookToken/CreateTokenForm';

const functions = getFunctions();
const getFacebookTokenStatus = httpsCallable(
  functions,
  'getFacebookTokenStatus',
);

const List = (props) => {
  const countryId = useSelector((state) => state.Dashboard.countryId);

  const [tokens, setTokens] = useState([]);
  const [summary, setSummary] = useState({
    total: 0,
    active: 0,
    warning: 0,
    critical: 0,
    expired: 0,
  });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [manualRenewalModal, setManualRenewalModal] = useState(false);
  const [createTokenModal, setCreateTokenModal] = useState(false);

  const [selectedCountryId, setSelectedCountryId] = useState(countryId);
  const selectedCountry = useSelector(selectCountry(selectedCountryId));

  useEffect(() => {
    loadTokenStatus();
  }, []);

  const loadTokenStatus = async () => {
    try {
      setRefreshing(true);

      const result = await getFacebookTokenStatus();

      if (result.data && result.data.tokens) {
        setTokens(result.data.tokens);
        setSummary(result.data.summary);
      }
    } catch (error) {
      console.error('Error loading token status:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleTokenRenewed = () => {
    loadTokenStatus();
  };

  const openManualRenewalModal = (countryId) => {
    setSelectedCountryId(countryId);
    setManualRenewalModal(true);
  };

  const handleManualRenewalSuccess = () => {
    setManualRenewalModal(false);
    loadTokenStatus();
  };

  const handleTokenCreated = () => {
    setCreateTokenModal(false);
    loadTokenStatus();
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid={true}>
          <Breadcrumbs
            title="Facebook Token Management"
            breadcrumbItem="Dashboard"
            onRefresh={loadTokenStatus}
            loading={refreshing}
          />

          {loading ? (
            <Card>
              <CardBody className="text-center">
                <Spinner color="primary" size="lg" />
                <p className="mt-3">🔄 Loading token status...</p>
              </CardBody>
            </Card>
          ) : (
            <>
              <Row className="mb-4">
                <Col md="12">
                  <TokenSummary summary={summary} />
                </Col>
              </Row>

              <Row>
                <Col className="col-12">
                  <Card>
                    <CardBody>
                      <Row className="align-items-center justify-content-between mb-3">
                        <Col>
                          <h4 className="card-title">
                            Token Status by Country
                          </h4>
                        </Col>
                        <Col className="text-right">
                          <Button
                            color="primary"
                            onClick={() => setCreateTokenModal(true)}
                            className="mr-2">
                            <i className="bx bx-plus mr-1"></i>
                            Create New Token
                          </Button>
                          <Spinner
                            color="secondary"
                            animation="border"
                            hidden={!refreshing}
                          />
                        </Col>
                      </Row>

                      {tokens.length === 0 ? (
                        <div className="text-center py-5">
                          <i
                            className="bx bx-info-circle"
                            style={{ fontSize: '48px', color: '#ccc' }}></i>
                          <p className="text-muted mt-3 mb-3">
                            No Facebook tokens configured
                          </p>
                          <Button
                            color="primary"
                            size="lg"
                            onClick={() => setCreateTokenModal(true)}>
                            <i className="bx bx-plus mr-2"></i>
                            Create First Token
                          </Button>
                        </div>
                      ) : (
                        <Row>
                          {tokens.map((token) => (
                            <Col
                              key={token.countryId}
                              xl="4"
                              lg="6"
                              md="12"
                              className="mb-4">
                              <TokenStatusCard
                                token={token}
                                country={selectedCountry}
                                onTokenRenewed={handleTokenRenewed}
                                onManualRenewal={openManualRenewalModal}
                              />
                            </Col>
                          ))}
                        </Row>
                      )}
                    </CardBody>
                  </Card>
                </Col>
              </Row>
            </>
          )}

          <Modal
            size="md"
            title={`Manual Token Renewal - ${selectedCountryId || ''}`}
            isOpen={manualRenewalModal}
            toggle={() => setManualRenewalModal(!manualRenewalModal)}
            showClose
            scrollable={false}>
            <ManualTokenRenewal
              countryId={selectedCountryId}
              onSuccess={handleManualRenewalSuccess}
            />
          </Modal>

          <Modal
            size="md"
            title="Create New Facebook Token"
            isOpen={createTokenModal}
            toggle={() => setCreateTokenModal(!createTokenModal)}
            showClose
            scrollable={false}>
            <CreateTokenForm
              countryId={countryId}
              onTokenCreated={handleTokenCreated}
              onCancel={() => setCreateTokenModal(false)}
            />
          </Modal>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default List;
